import { useEffect, useRef, useState } from 'react';

import { logger } from '@/lib/logger';

import { createProposalClient } from '../core/ProposalClient';
import type { ProposalExecutionStatus } from '../core/types';

interface UseExecutionPollingOptions {
  orgId: number;
  proposalId: number;
  enabled: boolean;
  interval?: number;
  onComplete?: (status: ProposalExecutionStatus) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for polling proposal execution status
 *
 * IMPORTANT: Multi-tab coordination is NOT implemented. Each browser tab
 * polls independently. If the same proposal is open in multiple tabs:
 * - All tabs will poll execution_status() independently (~N requests per interval)
 * - All tabs stop polling when execution completes
 * - This is acceptable for current usage (rare to open same proposal in 5+ tabs)
 *
 * Future optimization: Use BroadcastChannel API to coordinate across tabs
 * and avoid duplicate requests.
 *
 * Polls the execution_status endpoint until the proposal is no longer executing.
 * Uses refs for callbacks to avoid stale closures.
 *
 * @param options - Polling configuration
 * @returns Object with isPolling state and retry method
 *
 * @example
 * ```typescript
 * const { isPolling, retry } = useExecutionPolling({
 *   orgId: 123,
 *   proposalId: 456,
 *   enabled: true,
 *   interval: 1500,
 *   onComplete: (status) => {
 *     console.log('Execution finished:', status.resolution);
 *   },
 *   onError: (error) => {
 *     console.error('Polling error:', error);
 *   },
 * });
 * ```
 */
export function useExecutionPolling(options: UseExecutionPollingOptions): {
  isPolling: boolean;
  retry: () => void;
} {
  const {
    orgId,
    proposalId,
    enabled,
    interval = 1500,
    onComplete,
    onError,
  } = options;
  const [isPolling, setIsPolling] = useState(false);
  const [shouldPoll, setShouldPoll] = useState(enabled);

  // Use refs to avoid stale closures
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onCompleteRef.current = onComplete;
    onErrorRef.current = onError;
  }, [onComplete, onError]);

  // Sync shouldPoll with enabled prop
  useEffect(() => {
    setShouldPoll(enabled);
  }, [enabled]);

  useEffect(() => {
    if (!shouldPoll) {
      setIsPolling(false);
      return;
    }

    logger.info('[ExecutionPolling] started', {
      component: 'proposals',
      proposalId,
      intervalMs: interval,
    });

    const client = createProposalClient(orgId);
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let isMounted = true;
    let isPollingInProgress = false;

    const poll = async () => {
      // Prevent overlapping requests
      if (isPollingInProgress) {
        return;
      }

      isPollingInProgress = true;

      try {
        const status = await client.executionStatus(proposalId);

        logger.info('[ExecutionPolling] tick', {
          component: 'proposals',
          proposalId,
          resolution: status.resolution,
          isMounted,
        });

        // Check if component is still mounted before state updates
        if (!isMounted) {
          return;
        }

        if (status.resolution !== 'executing') {
          // Execution complete
          logger.info('[ExecutionPolling] complete — dispatching onComplete', {
            component: 'proposals',
            proposalId,
            resolution: status.resolution,
            hasOnComplete: !!onCompleteRef.current,
          });
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
          setIsPolling(false);
          setShouldPoll(false);
          onCompleteRef.current?.(status);
        }
      } catch (error) {
        logger.error(
          '[ExecutionPolling] poll failed',
          error instanceof Error ? error : new Error(String(error)),
          { component: 'proposals', proposalId, isMounted }
        );

        // Check if component is still mounted before state updates
        if (!isMounted) {
          return;
        }

        // Stop polling on error
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        setIsPolling(false);
        setShouldPoll(false);
        onErrorRef.current?.(
          error instanceof Error ? error : new Error('Polling failed')
        );
      } finally {
        isPollingInProgress = false;
      }
    };

    setIsPolling(true);
    poll(); // Poll immediately
    intervalId = setInterval(poll, interval);

    return () => {
      isMounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
      setIsPolling(false);
    };
  }, [shouldPoll, orgId, proposalId, interval]);

  const retry = () => {
    setShouldPoll(true);
  };

  return { isPolling, retry };
}
